import { useEffect, useState } from 'react';
import { api, darf, type Anmelder } from '../api.ts';
import { Dialog, Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';
import { Leer } from './Geruest.tsx';
import { useFrisch } from './Frisch.tsx';

/**
 * Wie ein Feld auf dem Bildschirm heisst.
 *
 * Die festgehaltene Abweichung trägt den SPALTENNAMEN, nicht die
 * Beschriftung: in der Tabelle steht, was die Datenbank kennt. Hier steht,
 * was ein Mensch liest. „kunde_telefon" in einer Zeile, die jemand
 * entscheiden soll, ist eine Zumutung.
 */
const TITEL: Record<string, string> = {
  kunde_name: 'Kundenname',
  kunde_telefon: 'Telefon',
  kunde_mobil: 'Mobil',
  kunde_email: 'E-Mail',
  nvt: 'NVt',
  nvt_adresse: 'NVt-Standort',
  rohrverband: 'Rohrverband',
  roehrchen: 'Röhrchen',
  roehrchenbreite: 'Röhrchenbreite',
};

/**
 * Das Deckblatt der Bauakte auslesen.
 *
 * ORIGINALTEXT MARVIN (20.08.2026)
 *   „aus jeder bauakte soll der punkt ,Röhrchenbreite' mit ausgelesen werden,
 *    dann in eine neue spalte eingetragen werden ... und dann soll das mit in
 *    die mobilen apps übertragen werden."
 *
 * ERST ANSEHEN, DANN ÜBERNEHMEN. Der Dialog zeigt beim Öffnen, WAS gefüllt
 * würde und WAS abweicht, und schreibt nichts. Genau diese Trennung fehlte im
 * Bestand, und deshalb hat ein danebengegangenes Muster einmal einen halben
 * Absatz in die Spalte „Röhrchen" geschrieben.
 *
 * KUNDENDATEN ÜBERSCHREIBEN NIE. Der erfasste Wert stammt meist vom Kunden
 * selbst, das Deckblatt ist Monate alt. Der Unterschied wird gezeigt und
 * einzeln entschieden.
 */
export function BauakteDialog({
  ich, auftragId, baustelle, zu, fertig,
}: {
  ich: Anmelder; auftragId: string; baustelle?: string;
  zu: () => void; fertig: () => void;
}) {
  const [d, setD] = useState<any>(null);
  const [offen, setOffen] = useState<any[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const darfScannen = darf(ich, 'bauakte.scannen');

  async function laden() {
    const [v, k] = await Promise.all([
      api.bauakte(auftragId), api.bauakteKonflikte(auftragId),
    ]);
    setD(v); setOffen(k);
  }
  useEffect(() => { laden().catch((f) => setFehler(ausFehler(f))); }, [auftragId]);
  useFrisch(laden);

  async function uebernehmen() {
    setLaeuft(true); setFehler(null);
    try { await api.bauakteLesen(auftragId); await laden(); fertig(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  async function entscheiden(id: string, ja: boolean) {
    setLaeuft(true); setFehler(null);
    try { await api.bauakteKonflikt(auftragId, id, ja); await laden(); fertig(); }
    catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Bauakte auslesen" untertitel={baustelle} breite={720}
            laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {!d ? <Leer text="Wird gelesen." /> : (
        <>
          {/* DREI ZUSTAENDE, NICHT ZWEI. „Es gab nichts zu fuellen" ist eine
              gute Nachricht, „die Akte gibt nichts her" eine Aufgabe, und
              „es liegt gar keine Akte da" ist ein WARTEN - sie kommt ueber
              den Posteingang. Alle drei mit demselben gelben Kasten zu
              zeigen hiesse, dem Innendienst drei verschiedene naechste
              Schritte als denselben zu verkaufen. */}
          <div className={'hinweis'
            + (d.keine_akte ? '' : d.gefuellt.length ? '' : ' warnung')}>
            <b>{d.meldung}</b>
            {d.dateiname && (
              <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
                Gelesen aus <code>{d.dateiname}</code>.
              </div>
            )}
          </div>

          {!!d.gefuellt.length && (
            <>
              <h4 className="klein leise" style={{ marginBottom: 6 }}>
                Das würde gefüllt
              </h4>
              <table className="liste">
                <thead>
                  <tr><th style={{ width: 220 }}>Feld</th><th>Wert</th></tr>
                </thead>
                <tbody>
                  {d.gefuellt.map((f: any) => (
                    <tr key={f.feld} style={{ cursor: 'default' }}>
                      <td>{f.titel}</td>
                      <td>{f.wert}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {darfScannen && (
                <div className="reihe" style={{ marginTop: 12 }}>
                  <button className="knopf haupt" disabled={laeuft}
                          onClick={uebernehmen}>
                    {laeuft ? 'Einen Moment'
                            : `${d.gefuellt.length} Felder übernehmen`}
                  </button>
                </div>
              )}
            </>
          )}

          {/* NICHTS VERSCHWINDET STILL. Was wegen einer Abweichung nicht
              geschrieben wurde, steht hier, mit beiden Werten nebeneinander
              und zwei Knöpfen. Ein Abhaken ohne Entscheidung liesse die Frage
              offen. */}
          {(!!d.konflikte.length || !!offen.length) && (
            <>
              <h4 className="klein leise" style={{ margin: '16px 0 6px' }}>
                Das weicht ab und wurde NICHT überschrieben
              </h4>
              <table className="liste">
                <thead>
                  <tr>
                    <th style={{ width: 170 }}>Feld</th>
                    <th>im System</th>
                    <th>in der Bauakte</th>
                    <th style={{ width: 230 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {offen.map((k: any) => (
                    <tr key={k.id} style={{ cursor: 'default' }}>
                      <td>{TITEL[k.feld] ?? k.feld}</td>
                      <td>{k.wert_system}</td>
                      <td>{k.wert_bauakte}</td>
                      <td>
                        {darfScannen && (
                          <div className="reihe">
                            <button className="knopf klein" disabled={laeuft}
                                    onClick={() => entscheiden(k.id, true)}>
                              Bauakte gilt
                            </button>
                            <button className="knopf klein" disabled={laeuft}
                                    onClick={() => entscheiden(k.id, false)}>
                              System gilt
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* Frisch erkannt, aber noch nicht festgehalten: das
                      passiert erst beim Übernehmen. */}
                  {d.konflikte
                    .filter((c: any) => !offen.some((k) => k.feld === c.feld))
                    .map((c: any) => (
                      <tr key={c.feld} style={{ cursor: 'default' }}>
                        <td>{c.titel}</td>
                        <td>{c.alt}</td>
                        <td>{c.neu}</td>
                        <td className="klein leise">
                          wird beim Übernehmen festgehalten
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}

          {!!d.leer.length && (
            <p className="klein leise" style={{ marginTop: 14 }}>
              Nicht im Deckblatt gefunden: {d.leer.join(', ')}. Das heisst
              nicht, dass es dort fehlt: ein reiner Scan ohne Textebene gibt
              gar nichts her.
            </p>
          )}
        </>
      )}
    </Dialog>
  );
}
